import { useApolloClient } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { makeClearNestedListFieldCache } from "utils/apollo/cacheHelpers";
import { assertPresence } from "utils/assertions";
import { DocumentType, gql } from "__generated__/gql";
import useCreateValidSubmitHandler, {
  UseCreateValidSubmitHandlerOptions,
} from "components/Shared/Forms/useCreateValidSubmitHandler";
import { SkuBrandOrderingPlatformFormFields, processApolloError } from "./Form";

const getOrderingPlatformQuery = gql(/* GraphQL */ `
  query GetOrderingPlatformIdForBrandOrderingPlatform(
    $brandOrderingPlatformId: UUID!
  ) {
    brandOrderingPlatform(id: $brandOrderingPlatformId) {
      id
      orderingPlatformId
    }
  }
`);

const createMutation = gql(/* GraphQL */ `
  mutation CreateSkuBrandOrderingPlatform(
    $id: UUID!
    $data: SkuBrandOrderingPlatformInput!
    $skuId: UUID!
    $orderingPlatformId: UUID!
    $distributionCenterIds: [UUID!]!
  ) {
    createSkuBrandOrderingPlatform(input: { skuBrandOrderingPlatform: $data }) {
      skuBrandOrderingPlatform {
        id
        ...EditSkuBrandOrderingPlatformDetails
      }
    }
    ...UpdateSkuBrandOrderingPlatformM2M
  }
`);

type CreateResult = DocumentType<typeof createMutation>;

type Params = {
  skuId?: UUID | null;
  brandOrderingPlatformId?: UUID | null;
};

export default function useCreateSkuBrandOrderingPlatform(
  {
    skuId: staticSkuId,
    brandOrderingPlatformId: staticBrandOrderingPlatformId,
  }: Params = {},
  options?: UseCreateValidSubmitHandlerOptions<CreateResult>,
) {
  const client = useApolloClient();

  const [performCreate, { loading: isCreating }] = useBrandMutation(
    createMutation,
    {
      update: (cache) => {
        const clearNestedListFieldCache = makeClearNestedListFieldCache(cache);
        clearNestedListFieldCache("Sku", [
          "skuBrandOrderingPlatforms",
          "skuBrandDistributionCenters",
        ]);
        clearNestedListFieldCache("BrandOrderingPlatform", [
          "skuBrandOrderingPlatforms",
        ]);
        clearNestedListFieldCache("BrandDistributionCenter", [
          "skuBrandDistributionCenters",
        ]);

        cache.modify({
          fields: (value: unknown, details) => {
            if (
              details.storeFieldName.includes("skuBrandOrderingPlatforms") ||
              details.storeFieldName.includes("skuBrandDistributionCenters")
            ) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              return details.DELETE;
            }
            return value;
          },
        });
      },
    },
  );

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    async (formData: SkuBrandOrderingPlatformFormFields) => {
      const {
        brandOrderingPlatformId: formBrandOrderingPlatformId,
        distributionCenterIds,
        skuId: formSkuId,
        ...sbopData
      } = formData;
      const id = uuidv4();

      const skuId = staticSkuId || formSkuId;
      const brandOrderingPlatformId =
        staticBrandOrderingPlatformId || formBrandOrderingPlatformId;

      assertPresence(skuId);
      assertPresence(brandOrderingPlatformId);

      const { data: bopData } = await client.query({
        query: getOrderingPlatformQuery,
        variables: { brandOrderingPlatformId },
      });

      const orderingPlatformId =
        bopData.brandOrderingPlatform?.orderingPlatformId;
      assertPresence(orderingPlatformId);

      const { data } = await performCreate({
        variables: {
          id,
          data: {
            ...sbopData,
            id,
            skuId,
            brandOrderingPlatformId,
          },
          distributionCenterIds,
          orderingPlatformId,
          skuId,
        },
      });
      assertPresence(data);
      return data;
    },
    {
      processApolloError,
      successMessage: "Product added successfully.",
      ...options,
    },
  );

  return { handleValidSubmit, isCreating };
}
